import { useEffect, useState } from "react";
import { Cookie, X, Shield, BarChart3 } from "lucide-react";

const STORAGE_KEY = "dea_cookie_consent_v1";

// Single source of truth for cookie categories.
// Update this when you add new tracking (es. GA, FB Pixel, Hotjar).
const CATEGORIES = [
  {
    id: "necessari",
    title: "Necessari",
    desc: "Cookie tecnici indispensabili per il funzionamento del sito (sessione, sicurezza). Non possono essere disattivati.",
    icon: Shield,
    required: true,
  },
  {
    id: "analytics",
    title: "Analitici",
    desc: "Ci aiutano a capire come i visitatori usano il sito (pagine viste, durata visita) in forma aggregata e anonima.",
    icon: BarChart3,
    required: false,
  },
];

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(state) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...state, ts: new Date().toISOString() })
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [details, setDetails] = useState(false);
  const [prefs, setPrefs] = useState({ necessari: true, analytics: false });

  useEffect(() => {
    const current = getConsent();
    if (!current) {
      // delay a moment to not jump in immediately on load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    saveConsent({ necessari: true, analytics: true });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ necessari: true, analytics: false });
    setVisible(false);
  };
  const saveCustom = () => {
    saveConsent(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[70] p-3 md:p-5 pointer-events-none"
    >
      <div className="max-w-[1100px] mx-auto bg-[#0A1120] border border-[#1E293B] shadow-2xl shadow-black/60 pointer-events-auto">
        {!details ? (
          <div className="p-5 md:p-7 grid md:grid-cols-12 gap-5 items-start">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                  <Cookie size={18} className="text-white" />
                </div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                  Privacy & Cookie
                </div>
              </div>
              <h3 className="text-white font-bold text-lg md:text-xl tracking-tight leading-snug">
                Utilizziamo i cookie per migliorare la tua esperienza
              </h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">
                Usiamo cookie tecnici necessari al funzionamento del sito e,
                con il tuo consenso, cookie analitici per capire come viene
                utilizzato. Puoi accettare, rifiutare o personalizzare le tue
                scelte. Maggiori informazioni nella nostra{" "}
                <a
                  href="/cookie-policy"
                  className="text-[#FF5A1F] underline hover:text-[#EA450C]"
                >
                  Cookie Policy
                </a>{" "}
                e{" "}
                <a
                  href="/privacy-policy"
                  className="text-[#FF5A1F] underline hover:text-[#EA450C]"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-5 py-3 text-sm font-semibold transition-colors"
              >
                Accetta tutti
              </button>
              <button
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="bg-transparent border border-slate-600 hover:border-white text-white px-5 py-3 text-sm font-semibold transition-colors"
              >
                Rifiuta non necessari
              </button>
              <button
                data-testid="cookie-customize"
                onClick={() => setDetails(true)}
                className="text-slate-400 hover:text-white text-xs underline self-start mt-1"
              >
                Personalizza preferenze
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 md:p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                  Preferenze cookie
                </div>
                <h3 className="text-white font-bold text-lg mt-1 tracking-tight">
                  Scegli cosa attivare
                </h3>
              </div>
              <button
                onClick={() => setDetails(false)}
                aria-label="Chiudi"
                className="text-slate-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const checked = prefs[c.id];
                return (
                  <label
                    key={c.id}
                    className="flex items-start gap-4 border border-[#1E293B] p-4 cursor-pointer hover:border-slate-600 transition-colors"
                  >
                    <div className="w-9 h-9 bg-[#111C30] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={16} className="text-[#FF5A1F]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-bold text-white text-sm">
                          {c.title}
                          {c.required && (
                            <span className="ml-2 text-[10px] tracking-wider uppercase text-slate-500 font-normal">
                              · sempre attivi
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          data-testid={`cookie-toggle-${c.id}`}
                          checked={checked}
                          disabled={c.required}
                          onChange={(e) =>
                            setPrefs({ ...prefs, [c.id]: e.target.checked })
                          }
                          className="w-5 h-5 accent-[#FF5A1F] disabled:opacity-60"
                        />
                      </div>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                        {c.desc}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                data-testid="cookie-save-prefs"
                onClick={saveCustom}
                className="bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-5 py-3 text-sm font-semibold transition-colors flex-1"
              >
                Salva preferenze
              </button>
              <button
                onClick={acceptAll}
                className="bg-transparent border border-slate-600 hover:border-white text-white px-5 py-3 text-sm font-semibold transition-colors flex-1"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
